import {
  ArrowLeftOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  Banner,
  Button,
  Chip,
  Heading,
  Input,
  KeywordsBroadMatchTable,
  Switch,
  Tooltip,
} from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { useTriggerCRUDActions } from '@mm/state/actions/TriggerCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import {
  getTriggerTitleByKind,
  TriggerKind,
} from '@mm/state/model/TriggerModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  instagramAndFacebookTriggersSelector,
  qAndATriggersSelector,
} from '@mm/state/selectors/triggerSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSupportedWidgets } from '../../../../../common/components/AddWidgetInsert/AddWidgetInsert';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { DialoguePreview } from '../../../../../modules/DailoguePreview/DialoguePreview';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import { useInputTriggerCRUD } from '../../../../features/beta/modules/Automations/AutomationEdit/InputTriggers/use-input-trigger-crud';
import { VIPs } from '../../../../features/configure/panels/sections/BotSettingsSection/VIPs/vips';
import {
  BROAD_MATCH,
  BROAD_MATCH_LABELS,
} from '../../../../features/startBotPromoters/commentGuards/components/EditFBPost/KeywordsForm/BroadMatch';
import { EmbeddableDialogueEditorWithPreview } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import { IG_SUPPORTED_WIDGET_TYPES } from '../../../../features/startBotPromoters/instagramStoryMention/InstagramStoryMentionEditor';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import styles from './QASlideoutEditor.module.css';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import {
  FBKeywordSlideoutBanner,
  IGKeywordSlideoutBanner,
} from '@mm/icons/SlideoutBanners';
import { useDeleteAutomationTrigger } from '../../../../features/beta/modules/Automations/AutomationEdit/InputTriggers/use-delete-automation-trigger';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
interface Props {
  triggerId?: number;
  campaignId?: number;
  audienceId?: number;
}

export const QASlideoutEditor = (props: Props) => {
  const qaTriggers = useSelector(qAndATriggersSelector);
  const fbIgTriggers = useSelector(instagramAndFacebookTriggersSelector);
  const triggers = [...qaTriggers, ...fbIgTriggers];

  const trigger = triggers.find(t => t.id === props.triggerId);
  const triggerCRUDActions = useTriggerCRUDActions();
  const dialogueId = trigger?.botResponses[0]?.pageId;
  const keywords = trigger?.phrases.map(p => p.phrase) || [];
  const [newKeyword, setNewKeyword] = useState('');
  const newKeywordInputRef = useRef<HTMLInputElement>(null);
  const updatingTrigger = useSelector(
    (state: StoreState) => state.trigger.busy,
  );
  const keywordInvalid =
    !newKeyword ||
    !!keywords.find(
      k =>
        k.localeCompare(newKeyword, undefined, { sensitivity: 'accent' }) === 0,
    );
  const { allWidgets: allSupportedWidgets } = useSupportedWidgets();
  const widgetsToExclude = allSupportedWidgets
    .filter(w => !IG_SUPPORTED_WIDGET_TYPES.includes(w.type))
    .map(w => w.type);
  const isInstagram = trigger?.kind === 'instagram_keywords';
  const viewActions = useViewActions();
  const { screenWidth } = useResolutionInfo();
  const { setRef } = useSlideoutOnClose();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;
  const heading = useMemo(
    () => getTriggerTitleByKind(trigger?.kind),
    [trigger?.kind],
  );
  const bannerHeader = `What are ${heading}?`;
  const modalActions = useModalActions();
  const campaign = useSelector((state: StoreState) =>
    state.campaigns.entities.find(c => c.id === props.campaignId),
  );
  const inputTriggerCRUD = useInputTriggerCRUD();
  const [shouldEditTrigger, setShouldEditTrigger] = useState(
    Boolean(
      trigger &&
        trigger.phrases.length === 1 &&
        trigger.phrases.find(
          trigger => trigger.phrase.toLocaleLowerCase() === 'keyword',
        ),
    ),
  );

  const getBannerProp = (triggerKind: TriggerKind) => {
    switch (triggerKind) {
      case 'facebook_keywords':
        return {
          component: <FBKeywordSlideoutBanner className={styles.phone_icon} />,
          css: styles.banner_facebook_keywords,
        };
      case 'instagram_keywords':
        return {
          component: <IGKeywordSlideoutBanner className={styles.phone_icon} />,
          css: styles.banner_instagram_keywords,
        };
      default:
        return {
          component: <></>,
          css: '',
        };
    }
  };

  useEffect(() => {
    if (shouldEditTrigger) {
      onRemoveKeyword(0);
    }
  }, []);

  const onAddKeyword = async () => {
    if (!trigger || updatingTrigger || keywordInvalid) {
      return;
    }

    await triggerCRUDActions.update({
      ...trigger,
      phrases: [
        ...trigger.phrases,
        { phrase: newKeyword, sortOrder: trigger.phrases.length },
      ],
    });

    setNewKeyword('');
    setTimeout(() => newKeywordInputRef.current?.focus(), 0);

    setShouldEditTrigger(false);
  };
  const onRemoveKeyword = async (keywordIndexToDelete: number) => {
    if (!trigger) {
      return;
    }
    const keywordToDelete = trigger.phrases[keywordIndexToDelete];
    setNewKeyword(newKeyword => newKeyword || keywordToDelete.phrase);

    const newPhrases = [...trigger.phrases];
    newPhrases.splice(keywordIndexToDelete, 1);

    await triggerCRUDActions.update({
      ...trigger,
      phrases: newPhrases.map((p, i) => ({ ...p, sortOrder: i })),
    });
    setTimeout(() => newKeywordInputRef.current?.focus(), 0);
  };

  const onStatusChange = async () => {
    await triggerCRUDActions.update({
      ...trigger,
      active: !trigger?.active,
    });
  };

  const { automationId, handleDelete } = useDeleteAutomationTrigger();

  const onDelete = async () => {
    if (!automationId) {
      const deleteCallback = async () => {
        if (trigger) {
          await triggerCRUDActions.delete(trigger);
        }
        viewActions.hideSlideoutMenu();
      };

      modalActions.openDeleteConfirmModal({
        headerText: 'Are you sure you want to delete this keyword trigger?',
        deleteCallback,
      });
    }

    if (automationId && trigger?.id) {
      await handleDelete('trigger', trigger.id, 'keyword trigger');
      viewActions.hideSlideoutMenu();
    }
  };

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <SlideoutMenu.Header className={styles.header} closeIcon="close">
        <Heading size={3} className={styles.heading}>
          {heading}
        </Heading>
        <Switch
          checked={trigger?.active || false}
          disabled={true}
          onChange={onStatusChange}
          label={trigger?.active ? 'active' : 'inactive'}
          css={{
            container: styles.switch,
            thumb: trigger?.active ? styles.thumbActive : styles.thumbInactive,
            label: trigger?.active
              ? styles.switchLabelActive
              : styles.switchLabelInactive,
          }}
        />
        {trigger ? (
          <div className={styles.deleteContainer} onClick={onDelete}>
            <DeleteOutlined />
          </div>
        ) : null}
      </SlideoutMenu.Header>

      <FreeTrialWarning />

      <SlideoutMenu.Content
        className={classNames(
          styles.content,
          isInstagram ? styles.contentInstagram : styles.contentFacebook,
        )}
      >
        {!isMobile && (
          <Banner
            css={{
              container: getBannerProp(trigger?.kind).css,
              header: getBannerProp(trigger?.kind).css,
              collapsedHeader: styles.collapsedHeader,
              content: styles.bannerContent,
              closeIcon: styles.bannerCloseIcon,
            }}
            withBannerHeader
            showCloseIcon
            usedInSlideout
            withLeftArea={false}
            headerText={bannerHeader}
          >
            {getBannerProp(trigger?.kind)?.component}
          </Banner>
        )}
        <div className={styles.container}>
          <div className={styles.configArea}>
            <CollapsibleContentContainer
              orderNumber="1"
              containerName="Enter Keywords that will trigger this automation when a contact sends them to start a conversation"
              className={styles.collapsibleContainer}
              stepperStylingPlatform={isInstagram ? 'instagram' : 'facebook'}
              useCircleNumberStyle
              expandedOnMount
            >
              {shouldEditTrigger && (
                <div className={styles.warning}>
                  <ExclamationCircleFilled className={styles.warningIcon} />
                  Please update the keyword below{' '}
                </div>
              )}
              <div className={styles.keywordInputRow}>
                <form
                  className={styles.keywordInputArea}
                  onSubmit={e => {
                    e.preventDefault();
                    onAddKeyword();
                  }}
                >
                  <Input
                    ref={newKeywordInputRef}
                    type="text"
                    placeholder="e.g. “buy a home”,"
                    css={{
                      container: styles.keywordsContainer,
                      input: styles.keywordsInput,
                    }}
                    value={newKeyword}
                    onChange={e => setNewKeyword(e.target.value)}
                    disabled={updatingTrigger}
                  />{' '}
                  <Button
                    type="submit"
                    color="blue"
                    disabled={updatingTrigger || keywordInvalid}
                    css={{ container: styles.addButton }}
                  >
                    Add
                  </Button>
                </form>
                <div className={styles.underInputLabelContainer}>
                  <div className={styles.keywordDefaultMatchInfo}>
                    Keywords use Exact Match Only
                  </div>
                  <Tooltip
                    css={{ container: styles.broadMatchTooltipContent }}
                    anchorElement={<InfoCircleOutlined />}
                    position="bottom"
                    align={screenWidth > 500 ? 'center' : 'end'}
                  >
                    <KeywordsBroadMatchTable
                      isMM
                      broadMatch={BROAD_MATCH}
                      labels={BROAD_MATCH_LABELS}
                    />
                  </Tooltip>
                </div>
              </div>

              <div className={styles.keywords}>
                {keywords.map((keyword, keywordIndex) => (
                  <Chip
                    key={keyword}
                    text={keyword}
                    iconProps={{ onClick: () => onRemoveKeyword(keywordIndex) }}
                  />
                ))}
              </div>
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              orderNumber="2"
              containerName="Reply instantly to users who send the keywords"
              className={styles.collapsibleContainer}
              stepperStylingPlatform={isInstagram ? 'instagram' : 'facebook'}
              useCircleNumberStyle
            >
              <EmbeddableDialogueEditorWithPreview
                className={styles.dialogueEditor}
                dialogueId={dialogueId}
                forInstagram={isInstagram}
                widgetsToExclude={isInstagram ? widgetsToExclude : []}
                platforms={isInstagram ? ['instagram'] : ['facebook']}
                usedInSlideout
                hideDialogueSelector
              />
            </CollapsibleContentContainer>
            {isInstagram && (
              <CollapsibleContentContainer
                orderNumber="3"
                containerName="Specify special sending rules"
                className={styles.collapsibleContainer}
                stepperStylingPlatform={isInstagram ? 'instagram' : 'facebook'}
                useCircleNumberStyle
              >
                <VIPs withTitle={false} className={styles.vip} />
              </CollapsibleContentContainer>
            )}
          </div>
          {!isMobile && dialogueId && (
            <DialoguePreview
              dialogueId={dialogueId}
              className={styles.dialoguePreview}
              sticky
            />
          )}
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
